import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {NavigateFunction} from 'react-router-dom';
import {EstimateTreeDto, TreeAdditionalDetails, WorkItemType} from '../../../shared/dtos/estimate-tree.dto';
import {EstimateTreeOperationDto, OperationAreaEntryDto} from '../../../shared/dtos/estimate-tree-operation.dto';
import {EstimateTreeEquipmentDto} from '../../../shared/dtos/estimate-tree-equipment.dto';
import {EstimateWithComponentsDto} from '../../../shared/dtos/estimate-with-components.dto';
import {EquipmentDto} from '../../equipment/dtos/equipment.dto';
import {PhotoEdit, saveImageEdit, uploadImage} from '../../../shared/services/image-service';
import {
  createTree, createTreeOperation, deleteTree, deleteTreeOperation, getTree, getTreeEquipment, getTreeOperations, updateEstimateTreeOperationsBulk,
  updateTree, updateTreeEquipment, updateTreeOperation,
} from '../estimate-tree.service';
import {distribute, distributeDollars, laborMinutes} from './record-math';
import {allPriced, laborTotalRefusal, parentStepRefusal} from './parent-adjust';
import {defaultDetails} from './operation-schema';
import {useAutosaveLanes} from './useAutosave';

/*
 * The work items of one estimate and every change to them, as Android's
 * WorkItemsController (sections/estimate/WorkItemsController.kt) makes them:
 *
 *  - adding or deleting a work item, an operation or a photo is sent at once;
 *  - a field, an answer, a note or a stepper tap changes the page at once and
 *    is saved after a quiet moment, one lane per thing being saved so a note
 *    and a stepper on the same operation never coalesce into one writer;
 *  - stored minutes are TOTAL labor (wall × crew); a stepper moves wall-clock
 *    time, the price follows at the crew's rate locally and the server's echo
 *    confirms it. With no crew there is no rate and the steppers say so.
 */

export interface WorkItemsState {
  trees: EstimateTreeDto[];
  /** The machines on each work item; the estimate's own read carries them, and every save brings them back. */
  equipmentByTree: Record<number, EstimateTreeEquipmentDto[]>;
  companyEquipment: EquipmentDto[];
  crewSize: number;
  crewSelected: boolean;
  laborRatePerHour: number | null;
  /**
   * SQ-636: the record's read's pricesMasked. True: its prices and estimated
   * time were hidden from this person. Absent (an API from before SQ-636):
   * nobody said they are visible. Either way a change to its time or prices
   * is refused before anything is sent; only false lets it through. From the
   * record's read only.
   */
  pricesMasked?: boolean;
  /** The work item a map tap or a new item brought forward; its card scrolls into view and clears it. */
  focusedTreeId?: number;
  /** Show Comparables & Generate AI Price was pressed: the AI rows show. */
  aiRequested: boolean;
  /** Photos still uploading, per work item (Android's "Uploading photos…"). */
  uploading: Record<number, number>;
  uploadFailed: Record<number, boolean>;
  /** Operations being added, per work item: their buttons spin and refuse a second tap until the add is in. */
  addingOperations: Record<number, string[]>;
  /** Operations whose add failed, per work item: "Couldn't add …" under its buttons for a few seconds. */
  failedOperations: Record<number, string[]>;
}

/** How long "Couldn't add …" stays under a work item's operation buttons. */
const ADD_FAILED_MS = 3000;

const SELECT_CREW = 'Select crew members to operate timer';
/** SQ-636: a time or price change on a record whose prices were hidden from the person (the apps' wording). */
const TIME_AND_PRICE_HIDDEN = "Your role can't see this job's prices, so it can't change its time.";
/** SQ-636: the same change where the server did not say whether the prices are shown to the person (the apps' wording). */
const TIME_AND_PRICE_UNCONFIRMED = "Couldn't confirm you can see this job's prices, so it can't change its time.";

const initialState = (estimate?: EstimateWithComponentsDto): WorkItemsState => ({
  trees: [...(estimate?.trees ?? [])].sort((a, b) => a.id - b.id),
  equipmentByTree: Object.fromEntries((estimate?.trees ?? []).map(t => [t.id, t.equipment ?? []])),
  companyEquipment: [],
  crewSize: Math.max(1, estimate?.crewSize ?? 1),
  crewSelected: (estimate?.crewMembers?.length ?? 0) > 0,
  laborRatePerHour: estimate?.laborRatePerHour ?? null,
  pricesMasked: estimate?.pricesMasked,
  aiRequested: false,
  uploading: {},
  uploadFailed: {},
  addingOperations: {},
  failedOperations: {},
});

/** The price at the crew's rate, as the server derives it (OperationPricing.CostFromMinutes). */
const withLocalPricing = (op: EstimateTreeOperationDto, minutes: number, rate: number | null): EstimateTreeOperationDto => {
  const cost = rate != null ? Math.round(minutes / 60 * rate) : undefined;
  return {
    ...op,
    estimatedMinutes: minutes,
    estimatedMinutesOverride: minutes,
    estimatedCost: cost ?? op.estimatedCost,
    estimatedCostOverride: cost ?? op.estimatedCostOverride,
  };
};

export type DetailValue = string | number | OperationAreaEntryDto[] | undefined;

export function useWorkItems(navigate: NavigateFunction, estimateId: number, onError: (message: string) => void) {
  const [state, setStateRaw] = useState<WorkItemsState>(initialState());
  const stateRef = useRef(state);
  const setState = useCallback((update: (s: WorkItemsState) => WorkItemsState) => {
    setStateRaw(prev => {
      const next = update(prev);
      stateRef.current = next;
      return next;
    });
  }, []);
  const lanes = useAutosaveLanes(onError);
  const errorRef = useRef(onError);
  errorRef.current = onError;
  const fail = (fallback: string) => (e: unknown) => errorRef.current(e instanceof Error && e.message ? e.message : fallback);

  const patchTree = useCallback((treeId: number, transform: (t: EstimateTreeDto) => EstimateTreeDto) =>
    setState(s => ({...s, trees: s.trees.map(t => t.id === treeId ? transform(t) : t)})), [setState]);

  const findOp = (treeId: number, opId: number) =>
    stateRef.current.trees.find(t => t.id === treeId)?.operations.find(o => o.id === opId);

  // ---- Loading ------------------------------------------------------------------------------

  /** Takes the estimate's work items (on load, or after something replaced them server-side). */
  const initialize = useCallback((estimate: EstimateWithComponentsDto) => {
    setState(s => ({
      ...initialState(estimate), companyEquipment: s.companyEquipment, aiRequested: s.aiRequested,
      addingOperations: s.addingOperations, failedOperations: s.failedOperations,
    }));
  }, [setState]);

  const setCompanyEquipment = useCallback((equipment: EquipmentDto[]) =>
    setState(s => ({...s, companyEquipment: equipment})), [setState]);

  /** Crew picked or changed: the count divides the shown time; none picked stops the steppers. */
  const setCrew = useCallback((selectedCount: number, rate: number | null) =>
    setState(s => ({...s, crewSize: Math.max(1, selectedCount), crewSelected: selectedCount > 0, laborRatePerHour: rate})), [setState]);

  const refreshTree = useCallback(async (treeId: number) => {
    const [fresh, equipment] = await Promise.all([
      getTree(navigate, estimateId, treeId),
      getTreeEquipment(navigate, estimateId, treeId).catch(() => undefined),
    ]);
    setState(s => ({
      ...s,
      trees: s.trees.map(t => t.id === treeId ? {...fresh, equipment: equipment ?? t.equipment} : t),
      equipmentByTree: equipment ? {...s.equipmentByTree, [treeId]: equipment} : s.equipmentByTree,
    }));
  }, [navigate, estimateId, setState]);

  /** After the crew changed the server re-derived every operation's minutes from its price: bring them down. */
  const refreshAllTrees = useCallback(() => {
    stateRef.current.trees.forEach(t => void refreshTree(t.id).catch(fail('Failed to refresh work item')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTree]);

  const consumeFocus = useCallback(() => setState(s => ({...s, focusedTreeId: undefined})), [setState]);
  const focus = useCallback((treeId: number) => setState(s => ({...s, focusedTreeId: treeId})), [setState]);
  const markAiRequested = useCallback(() => setState(s => ({...s, aiRequested: true})), [setState]);

  // ---- The map's changes ---------------------------------------------------------------------

  /**
   * The map moved, added or removed work items (its own add menu, a drag):
   * take their places and the ones it created; drop the ones it deleted.
   */
  const syncFromMap = useCallback((mapTrees: EstimateTreeDto[]) => setState(s => {
    const byId = new Map(mapTrees.map(t => [t.id, t]));
    const kept = s.trees.filter(t => byId.has(t.id)).map(t => {
      const m = byId.get(t.id)!;
      return m.lat === t.lat && m.lng === t.lng && m.dragDistanceFeet === t.dragDistanceFeet
        ? t : {...t, lat: m.lat, lng: m.lng, dragDistanceFeet: m.dragDistanceFeet};
    });
    const added = mapTrees.filter(t => !s.trees.some(o => o.id === t.id));
    if (added.length === 0 && kept.length === s.trees.length && kept.every((t, i) => t === s.trees[i])) return s;
    return {
      ...s,
      trees: [...kept, ...added].sort((a, b) => a.id - b.id),
      equipmentByTree: {...s.equipmentByTree, ...Object.fromEntries(added.map(t => [t.id, t.equipment ?? []]))},
    };
  }), [setState]);

  // ---- Work items -----------------------------------------------------------------------------

  /** Creates the work item at once (no draft) and brings its card forward. */
  const addWorkItem = useCallback(async (type: WorkItemType, lat: number, lng: number) => {
    try {
      const created = await createTree(navigate, estimateId, {workItemType: type, lat, lng});
      setState(s => ({
        ...s,
        trees: [...s.trees, {...created, equipment: created.equipment ?? []}],
        equipmentByTree: {...s.equipmentByTree, [created.id]: created.equipment ?? []},
        focusedTreeId: created.id,
      }));
      return created;
    } catch (e) {
      fail('Failed to add work item')(e);
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, estimateId, setState]);

  const deleteWorkItem = useCallback(async (treeId: number) => {
    const tree = stateRef.current.trees.find(t => t.id === treeId);
    lanes.cancel(`tree-${treeId}`);
    lanes.cancel(`bulk-tree-${treeId}`);
    lanes.cancel(`equipment-${treeId}`);
    tree?.operations.forEach(op => ['minutes', 'details', 'notes'].forEach(k => lanes.cancel(`${k}-${op.id}`)));
    try {
      await deleteTree(navigate, estimateId, treeId);
      setState(s => ({...s, trees: s.trees.filter(t => t.id !== treeId)}));
      return true;
    } catch (e) {
      fail('Failed to delete work item')(e);
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, estimateId, setState, lanes]);

  /** `at` overrides the stored spot: a move saves where it put the item, not what the state may still hold. */
  const persistTree = useCallback(async (treeId: number, at?: {lat: number; lng: number}) => {
    const found = stateRef.current.trees.find(t => t.id === treeId);
    if (!found) return;
    const tree = at ? {...found, ...at} : found;
    // Not re-applying the echo: the person may still be typing.
    await updateTree(navigate, estimateId, treeId, {
      estimateId, treeId, lat: tree.lat, lng: tree.lng,
      height: tree.height, width: tree.width, trunkDiameter: tree.trunkDiameter, species: tree.species,
      notes: tree.notes ?? '', crewNotes: tree.crewNotes ?? '', additionalDetails: tree.additionalDetails,
    } as never);
  }, [navigate, estimateId]);

  /** A field on the work item (a measurement, a note, a Work Plan answer): shown at once, saved after a moment. */
  const changeTree = useCallback((treeId: number, transform: (t: EstimateTreeDto) => EstimateTreeDto) => {
    patchTree(treeId, transform);
    lanes.schedule(`tree-${treeId}`, () => persistTree(treeId));
  }, [patchTree, lanes, persistTree]);

  /**
   * The map moved the work item (SQ-621): a drop is one gesture, so it is
   * saved at once, and put back where it was if the server refuses it.
   * Resolves true once the new spot is saved.
   */
  const moveWorkItem = useCallback(async (treeId: number, lat: number, lng: number) => {
    const before = stateRef.current.trees.find(t => t.id === treeId);
    if (!before) return false;
    patchTree(treeId, t => ({...t, lat, lng}));
    try {
      await persistTree(treeId, {lat, lng});
      return true;
    } catch (e) {
      patchTree(treeId, t => ({...t, lat: before.lat, lng: before.lng}));
      fail('Failed to move work item')(e);
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchTree, persistTree]);

  /** The drag distance the map measured: the record's "Drag Distance 120 ft" (the route's own save stores it). */
  const setDragDistanceFeet = useCallback((treeId: number, feet: number | undefined) =>
    patchTree(treeId, t => t.dragDistanceFeet === feet ? t : {...t, dragDistanceFeet: feet}), [patchTree]);

  /** Sets Work Plan answers (several at once are one edit, one save). */
  const setDetails = useCallback((treeId: number, values: Partial<TreeAdditionalDetails>) =>
    changeTree(treeId, t => ({...t, additionalDetails: {...(t.additionalDetails ?? {}), ...values}})), [changeTree]);

  // ---- Photos ------------------------------------------------------------------------------------

  /** Uploads the photos, then attaches whatever reached the store (even when a later one failed). */
  const uploadPhotos = useCallback(async (treeId: number, files: File[]) => {
    setState(s => ({...s, uploading: {...s.uploading, [treeId]: (s.uploading[treeId] ?? 0) + files.length}, uploadFailed: {...s.uploadFailed, [treeId]: false}}));
    const uploaded: number[] = [];
    let failed = false;
    for (const file of files) {
      try {
        uploaded.push((await uploadImage(file)).id);
      } catch {
        failed = true;
        break;
      }
    }
    try {
      if (uploaded.length > 0) {
        await updateTree(navigate, estimateId, treeId, {imageIdsToAdd: uploaded} as never);
        await refreshTree(treeId);
      }
    } catch {
      failed = true;
    }
    setState(s => ({
      ...s,
      uploading: {...s.uploading, [treeId]: Math.max(0, (s.uploading[treeId] ?? 0) - files.length)},
      uploadFailed: {...s.uploadFailed, [treeId]: failed},
    }));
  }, [navigate, estimateId, setState, refreshTree]);

  const deletePhoto = useCallback(async (treeId: number, imageId: number) => {
    try {
      await updateTree(navigate, estimateId, treeId, {imageIdsToRemove: [imageId]} as never);
      patchTree(treeId, t => ({...t, images: t.images.filter(i => i.id !== imageId)}));
    } catch (e) {
      fail('Failed to delete photo')(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, estimateId, patchTree]);

  /** SQ-286: the edited copy replaces the photo in place (same id, same slot); the server keeps the original and the marks. */
  const replacePhoto = useCallback(async (treeId: number, imageId: number, edit: PhotoEdit) => {
    const saved = await saveImageEdit(imageId, edit);
    patchTree(treeId, t => ({...t, images: t.images.map(i => i.id === imageId ? {...i, ...saved} : i)}));
  }, [patchTree]);

  // ---- Operations ----------------------------------------------------------------------------------

  const failedTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  useEffect(() => () => Object.values(failedTimers.current).forEach(clearTimeout), []);

  /** A tap on any of a work item's operation buttons takes its "Couldn't add …" away. */
  const clearFailedOperations = useCallback((treeId: number) => {
    clearTimeout(failedTimers.current[treeId]);
    setState(s => (s.failedOperations[treeId] ? {...s, failedOperations: {...s.failedOperations, [treeId]: []}} : s));
  }, [setState]);

  const setAdding = useCallback((treeId: number, operationType: string, adding: boolean) =>
    setState(s => {
      const now = (s.addingOperations[treeId] ?? []).filter(t => t !== operationType);
      return {...s, addingOperations: {...s.addingOperations, [treeId]: adding ? [...now, operationType] : now}};
    }), [setState]);

  /**
   * Adds an instance, seeded with the schema's defaults ([notes]: the
   * stump-grinding waiver). While it saves, its button spins and refuses a
   * second tap; other operations can be added meanwhile, each appearing as
   * soon as its own save is in. A failed add asks the server first: on a
   * weak signal the save may have landed while its answer was lost, and
   * then the operation simply appears (no second copy from a re-tap).
   */
  const addOperation = useCallback(async (treeId: number, operationType: string, notes?: string) => {
    clearFailedOperations(treeId);
    if (stateRef.current.addingOperations[treeId]?.includes(operationType)) return;
    setAdding(treeId, operationType, true);
    const known = new Set(stateRef.current.trees.find(t => t.id === treeId)?.operations.map(o => o.id) ?? []);
    let created: EstimateTreeOperationDto | undefined;
    try {
      created = await createTreeOperation(navigate, estimateId, treeId, {
        treeId, operationType: operationType as never, details: defaultDetails(operationType), notes,
      });
    } catch {
      created = await getTreeOperations(navigate, estimateId, treeId)
        .then(ops => ops.find(o => o.operationType === operationType && !known.has(o.id)))
        .catch(() => undefined);
    }
    setAdding(treeId, operationType, false);
    if (created) {
      const op = created;
      patchTree(treeId, t => (t.operations.some(o => o.id === op.id) ? t : {...t, operations: [...t.operations, op]}));
      return;
    }
    setState(s => ({...s, failedOperations: {...s.failedOperations, [treeId]: [...(s.failedOperations[treeId] ?? []), operationType]}}));
    clearTimeout(failedTimers.current[treeId]);
    failedTimers.current[treeId] = setTimeout(() => clearFailedOperations(treeId), ADD_FAILED_MS);
  }, [navigate, estimateId, patchTree, setState, setAdding, clearFailedOperations]);

  const deleteOperation = useCallback(async (treeId: number, opId: number) => {
    ['minutes', 'details', 'notes'].forEach(k => lanes.cancel(`${k}-${opId}`));
    try {
      await deleteTreeOperation(navigate, estimateId, treeId, opId);
      patchTree(treeId, t => ({...t, operations: t.operations.filter(o => o.id !== opId)}));
    } catch (e) {
      fail('Failed to delete operation')(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, estimateId, patchTree, lanes]);

  /** Answers in the operation's details (a pick, an area list with its mirror keys, a details box). */
  const changeOperationDetails = useCallback((treeId: number, opId: number, patch: Record<string, DetailValue>) => {
    patchTree(treeId, t => ({
      ...t,
      operations: t.operations.map(o => o.id === opId ? {...o, details: {...(o.details ?? {}), ...patch} as never} : o),
    }));
    lanes.schedule(`details-${opId}`, async () => {
      const op = findOp(treeId, opId);
      if (!op) return;
      await updateTreeOperation(navigate, estimateId, treeId, opId, {treeId, operationType: op.operationType, details: op.details});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchTree, lanes, navigate, estimateId]);

  /** SQ-487 §5c: the operation's own note. A cleared note travels as "". */
  const changeOperationNotes = useCallback((treeId: number, opId: number, notes: string) => {
    patchTree(treeId, t => ({...t, operations: t.operations.map(o => o.id === opId ? {...o, notes: notes as never} : o)}));
    lanes.schedule(`notes-${opId}`, async () => {
      const op = findOp(treeId, opId);
      if (!op) return;
      await updateTreeOperation(navigate, estimateId, treeId, opId,
        {treeId, operationType: op.operationType, details: op.details, notes: (op.notes as string | undefined) ?? ''} as never);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchTree, lanes, navigate, estimateId]);

  /**
   * SQ-636: true (and the page says why) unless the record's read said its
   * prices are shown to the person. Hidden, the server would refuse the
   * change; not said (an older API), a change made from figures it may have
   * hidden would overwrite the real ones. Checked before anything moves, and
   * before the job page asks "Change the job total?".
   */
  const refusesTimeAndPrice = useCallback(() => {
    const masked = stateRef.current.pricesMasked;
    if (masked === false) return false;
    errorRef.current(masked ? TIME_AND_PRICE_HIDDEN : TIME_AND_PRICE_UNCONFIRMED);
    return true;
  }, []);

  /**
   * Refuses (and says why) a time step: hidden prices first (such a record is
   * sent no rate either, and a crew would not help), then no crew, and so no
   * rate to price time at.
   */
  const blocked = () => {
    if (refusesTimeAndPrice()) return true;
    const s = stateRef.current;
    if (s.crewSelected && s.laborRatePerHour != null && s.laborRatePerHour > 0) return false;
    errorRef.current(SELECT_CREW);
    return true;
  };

  const persistOperationMinutes = useCallback(async (treeId: number, opId: number) => {
    const op = findOp(treeId, opId);
    if (!op) return;
    const minutes = op.estimatedMinutes ?? laborMinutes(op, stateRef.current.laborRatePerHour);
    const updated = await updateTreeOperation(navigate, estimateId, treeId, opId,
      {estimatedMinutes: minutes, estimatedMinutesOverride: minutes} as never);
    // Only the server's prices come back: the minutes stay local so a tap racing this response is not undone.
    patchTree(treeId, t => ({
      ...t,
      operations: t.operations.map(o => o.id === opId
        ? {...o, estimatedCost: updated.estimatedCost, aiEstimatedCost: updated.aiEstimatedCost, estimatedCostOverride: updated.estimatedCostOverride}
        : o),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, estimateId, patchTree]);

  /** An operation's stepper: `deltaWall` wall-clock minutes, stored as wall × crew labor minutes. */
  const stepOperation = useCallback((treeId: number, opId: number, deltaWall: number) => {
    if (blocked()) return;
    const {crewSize, laborRatePerHour: rate} = stateRef.current;
    patchTree(treeId, t => ({
      ...t,
      operations: t.operations.map(o => o.id === opId
        ? withLocalPricing(o, Math.max(0, laborMinutes(o, rate) + deltaWall * crewSize), rate) : o),
    }));
    lanes.schedule(`minutes-${opId}`, () => persistOperationMinutes(treeId, opId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchTree, lanes, persistOperationMinutes]);

  const persistBulkMinutes = useCallback(async (treeIds: number[]) => {
    const {trees, laborRatePerHour: rate} = stateRef.current;
    const entries = trees.filter(t => treeIds.includes(t.id)).flatMap(t => t.operations.map(op => {
      const minutes = laborMinutes(op, rate);
      return {treeId: t.id, operationId: op.id, estimatedMinutes: minutes, estimatedMinutesOverride: minutes};
    }));
    if (entries.length === 0) return;
    const response = await updateEstimateTreeOperationsBulk(navigate, estimateId, entries);
    const fresh = new Map(response.flatMap(r => r.operations).map(o => [o.id, o]));
    setState(s => ({
      ...s,
      trees: s.trees.map(t => ({
        ...t,
        operations: t.operations.map(o => {
          const f = fresh.get(o.id);
          return f ? {...o, estimatedCost: f.estimatedCost, aiEstimatedCost: f.aiEstimatedCost, estimatedCostOverride: f.estimatedCostOverride} : o;
        }),
      })),
    }));
  }, [navigate, estimateId, setState]);

  /** Scales the labor of `treeIds`' operations by `deltaWall` wall minutes, spread by their shares. */
  const distributeOver = (treeIds: number[], deltaWall: number) => {
    const {trees, crewSize, laborRatePerHour: rate} = stateRef.current;
    const ops = trees.filter(t => treeIds.includes(t.id)).flatMap(t => t.operations);
    if (ops.length === 0) return false;
    const current = ops.map(o => laborMinutes(o, rate));
    const target = Math.max(0, current.reduce((a, b) => a + b, 0) + deltaWall * crewSize);
    const shares = distribute(current, target);
    const byOp = new Map(ops.map((o, i) => [o.id, shares[i]]));
    setState(s => ({
      ...s,
      trees: s.trees.map(t => ({
        ...t,
        operations: t.operations.map(o => byOp.has(o.id) ? withLocalPricing(o, byOp.get(o.id)!, rate) : o),
      })),
    }));
    return true;
  };

  /**
   * SQ-660: a parent timer spreads by the operations' prices, so it moves
   * nothing while a work item has no operation or an operation no price,
   * nor when a step down would take one to $0 (the timer itself says which:
   * parent-adjust.ts).
   */
  const parentStepAllowed = (treeIds: number[], deltaWall: number) => {
    const {trees, crewSize, laborRatePerHour: rate} = stateRef.current;
    const scope = trees.filter(t => treeIds.includes(t.id));
    return allPriced(scope)
      && parentStepRefusal(scope, trees, true, deltaWall, crewSize, rate) === undefined;
  };

  /** The work item's total stepper: its labor moves by `deltaWall`, spread over its operations by share. */
  const stepTree = useCallback((treeId: number, deltaWall: number) => {
    if (blocked()) return;
    if (!parentStepAllowed([treeId], deltaWall)) return;
    if (!distributeOver([treeId], deltaWall)) return;
    lanes.schedule(`bulk-tree-${treeId}`, () => persistBulkMinutes([treeId]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanes, persistBulkMinutes]);

  /** The Job Total's stepper: every operation, by share. */
  const stepEstimate = useCallback((deltaWall: number) => {
    if (blocked()) return;
    const ids = stateRef.current.trees.map(t => t.id);
    if (!parentStepAllowed(ids, deltaWall)) return;
    if (!distributeOver(ids, deltaWall)) return;
    lanes.schedule('bulk-estimate', () => persistBulkMinutes(ids));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanes, persistBulkMinutes]);

  /**
   * SQ-402, a typed Job Total: only labor moves. `laborTotal` (whole dollars)
   * spreads over every operation's price by one factor; the server works each
   * operation's time back from its price.
   *
   * SQ-660: it needs every operation priced (never the even split) and refuses
   * a total that would take one to $0; the field says why. It needs no crew:
   * with none there is no rate, so only the prices move, the times stay as
   * stored (shown as a dash), and the server works them out from the prices
   * once a crew is picked.
   */
  const setLaborTotal = useCallback((laborTotal: number) => {
    if (refusesTimeAndPrice()) return;
    const {trees, crewSelected} = stateRef.current;
    const rate = crewSelected && stateRef.current.laborRatePerHour ? stateRef.current.laborRatePerHour : undefined;
    const ops = trees.flatMap(t => t.operations);
    if (ops.length === 0 || !allPriced(trees) || laborTotalRefusal(trees, laborTotal)) return;
    const prices = distributeDollars(ops.map(o => Math.round(o.estimatedCost ?? 0)), Math.max(0, Math.round(laborTotal)));
    const byOp = new Map(ops.map((o, i) => [o.id, prices[i]]));
    setState(s => ({
      ...s,
      trees: s.trees.map(t => ({
        ...t,
        operations: t.operations.map(o => {
          const price = byOp.get(o.id);
          if (price == null) return o;
          if (!rate) return {...o, estimatedCost: price, estimatedCostOverride: price};
          const minutes = Math.round(price / rate * 60);
          return {...o, estimatedCost: price, estimatedCostOverride: price, estimatedMinutes: minutes, estimatedMinutesOverride: minutes};
        }),
      })),
    }));
    lanes.schedule('bulk-estimate', async () => {
      const entries = stateRef.current.trees.flatMap(t => t.operations.map(o => ({
        treeId: t.id, operationId: o.id, estimatedCost: o.estimatedCost ?? 0, estimatedCostOverride: o.estimatedCost ?? 0,
      })));
      // No echo: the prices are what was sent, and a racing edit must not be undone by an older answer.
      if (entries.length > 0) await updateEstimateTreeOperationsBulk(navigate, estimateId, entries);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setState, lanes, navigate, estimateId]);

  /** "AI Time & Price": the AI's time, overrides cleared, so the operation is the AI's again. */
  const chooseAi = useCallback(async (treeId: number, opId: number) => {
    if (refusesTimeAndPrice()) return;
    const op = findOp(treeId, opId);
    const aiMinutes = op?.aiEstimatedMinutes;
    if (!op || aiMinutes == null) return;
    lanes.cancel(`minutes-${opId}`);
    patchTree(treeId, t => ({
      ...t,
      operations: t.operations.map(o => o.id === opId
        ? {...o, estimatedMinutes: aiMinutes, estimatedMinutesOverride: undefined, estimatedCost: op.aiEstimatedCost, estimatedCostOverride: undefined}
        : o),
    }));
    try {
      await updateTreeOperation(navigate, estimateId, treeId, opId, {estimatedMinutes: aiMinutes, clearOverrides: true} as never);
    } catch (e) {
      fail('Failed to switch to AI pricing')(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchTree, lanes, navigate, estimateId]);

  /** SignalR: an operation's AI estimate moved on; on Completed the tree is read again so its minutes and price arrive together. */
  const applyAiStatus = useCallback((treeId: number, opId: number, status: string) => {
    if (!stateRef.current.trees.some(t => t.id === treeId)) return;
    patchTree(treeId, t => ({
      ...t,
      operations: t.operations.map(o => o.id === opId ? {...o, aiEstimatedCostGenerationStatus: status as never} : o),
    }));
    if (status === 'Completed') void refreshTree(treeId).catch(() => undefined);
  }, [patchTree, refreshTree]);

  // ---- Equipment --------------------------------------------------------------------------------------

  // Work items whose machines' actual time changed since their last save: the
  // server writes actual time only when asked, and a later billed-time edit
  // in the same save lane must not drop the request.
  const actualsDirty = useRef(new Set<number>());

  const persistEquipment = useCallback(async (treeId: number) => {
    const rows = stateRef.current.equipmentByTree[treeId] ?? [];
    const actuals = actualsDirty.current.delete(treeId);
    const fresh = await updateTreeEquipment(navigate, estimateId, treeId, rows.map(r => ({
      equipmentId: r.equipmentId, durationMinutes: r.durationMinutes, actualDurationMinutes: r.actualDurationMinutes,
    }) as never), actuals);
    setState(s => ({...s, equipmentByTree: {...s.equipmentByTree, [treeId]: fresh}}));
  }, [navigate, estimateId, setState]);

  /**
   * A machine's time on the work item, adding it when it is not there yet.
   * Zero keeps the row: it holds the rate locked when the machine was picked,
   * so a slip to zero and back never re-locks it at today's rate.
   */
  const setEquipmentMinutes = useCallback((treeId: number, equipmentId: number, minutes: number) => {
    if (refusesTimeAndPrice()) return;
    setState(s => {
      const current = s.equipmentByTree[treeId] ?? [];
      const clamped = Math.max(0, minutes);
      const existing = current.find(r => r.equipmentId === equipmentId);
      const machine = s.companyEquipment.find(e => e.id === equipmentId);
      const rows = existing
        ? current.map(r => r.equipmentId === equipmentId ? {...r, durationMinutes: clamped} : r)
        : [...current, {
          id: 0, estimateId, treeId, equipmentId, durationMinutes: clamped, actualDurationMinutes: null,
          hourlyRate: machine?.hourlyRate ?? null, minimumBillableMinutes: 0, minimumCharge: machine?.minimumCharge ?? null,
          currentMinimumCharge: machine?.minimumCharge ?? null, wholeDollars: true, origin: 'assigned',
          equipmentNickname: machine?.nickname ?? null, equipmentType: machine?.type ?? null,
          currentHourlyRate: machine?.hourlyRate ?? null, currentMinimumBillableMinutes: machine?.minimumBillableMinutes ?? null,
          rateChanged: false, createdBy: 0, createdAt: '', updatedAt: '',
        } as EstimateTreeEquipmentDto];
      return {...s, equipmentByTree: {...s.equipmentByTree, [treeId]: rows}};
    });
    lanes.schedule(`equipment-${treeId}`, () => persistEquipment(treeId));
  }, [setState, lanes, persistEquipment, estimateId, refusesTimeAndPrice]);

  /** Steps against the time in state now, not the one the button was drawn with. */
  const stepEquipment = useCallback((treeId: number, equipmentId: number, delta: number) => {
    const current = stateRef.current.equipmentByTree[treeId]?.find(r => r.equipmentId === equipmentId)?.durationMinutes ?? 0;
    setEquipmentMinutes(treeId, equipmentId, current + delta);
  }, [setEquipmentMinutes]);

  /** Takes a machine off the work item; adding it back locks the rate as of then. */
  const removeEquipment = useCallback((treeId: number, equipmentId: number) => {
    if (refusesTimeAndPrice()) return;
    setState(s => ({...s, equipmentByTree: {...s.equipmentByTree, [treeId]: (s.equipmentByTree[treeId] ?? []).filter(r => r.equipmentId !== equipmentId)}}));
    lanes.schedule(`equipment-${treeId}`, () => persistEquipment(treeId));
  }, [setState, lanes, persistEquipment, refusesTimeAndPrice]);

  // ---- SQ-530 / SQ-597: equipment's actual time, while the job is reviewed ----------------------------

  /** The time a machine actually ran (null: what it is billed for). The billed time and every total stay. */
  const setEquipmentActual = useCallback((treeId: number, equipmentId: number, minutes: number | null) => {
    if (refusesTimeAndPrice()) return;
    setState(s => ({
      ...s,
      equipmentByTree: {
        ...s.equipmentByTree,
        [treeId]: (s.equipmentByTree[treeId] ?? []).map(r => r.equipmentId === equipmentId
          ? {...r, actualDurationMinutes: minutes == null ? null : Math.max(0, minutes)} : r),
      },
    }));
    actualsDirty.current.add(treeId);
    lanes.schedule(`equipment-${treeId}`, () => persistEquipment(treeId));
  }, [setState, lanes, persistEquipment, refusesTimeAndPrice]);

  /** Steps the actual time against the value in state now (a held ± repeats). */
  const stepEquipmentActual = useCallback((treeId: number, equipmentId: number, delta: number) => {
    const row = stateRef.current.equipmentByTree[treeId]?.find(r => r.equipmentId === equipmentId);
    if (row) setEquipmentActual(treeId, equipmentId, (row.actualDurationMinutes ?? row.durationMinutes) + delta);
  }, [setEquipmentActual]);

  /** Saving out of Edit in review makes what was saved the new baseline: every machine ran what it is billed. */
  const clearEquipmentActuals = useCallback(() => {
    // Part of Save, not a tap: a person the prices are hidden from (or not said to be shown) could not have set any.
    if (stateRef.current.pricesMasked !== false) return;
    const touched = Object.entries(stateRef.current.equipmentByTree)
      .filter(([, rows]) => rows.some(r => r.actualDurationMinutes != null)).map(([id]) => Number(id));
    if (touched.length === 0) return;
    setState(s => ({
      ...s,
      equipmentByTree: Object.fromEntries(Object.entries(s.equipmentByTree)
        .map(([id, rows]) => [id, rows.map(r => ({...r, actualDurationMinutes: null}))])),
    }));
    touched.forEach(treeId => actualsDirty.current.add(treeId));
    touched.forEach(treeId => lanes.schedule(`equipment-${treeId}`, () => persistEquipment(treeId)));
  }, [setState, lanes, persistEquipment]);

  const trees = useMemo(() => state.trees.map(t => ({...t, equipment: state.equipmentByTree[t.id] ?? t.equipment ?? []})),
    [state.trees, state.equipmentByTree]);

  return {
    state, trees,
    initialize, setCompanyEquipment, setCrew, refreshTree, refusesTimeAndPrice, refreshAllTrees, consumeFocus, focus, markAiRequested, syncFromMap,
    addWorkItem, deleteWorkItem, moveWorkItem, setDragDistanceFeet, changeTree, setDetails, uploadPhotos, deletePhoto, replacePhoto,
    addOperation, deleteOperation, changeOperationDetails, changeOperationNotes, stepOperation, stepTree, stepEstimate,
    setLaborTotal, chooseAi, applyAiStatus, setEquipmentMinutes, stepEquipment, removeEquipment,
    setEquipmentActual, stepEquipmentActual, clearEquipmentActuals,
    flush: lanes.flush,
  };
}

export type WorkItems = ReturnType<typeof useWorkItems>;
